import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useProfile } from '../contexts/ProfileContext';
import { useTheme } from '../contexts/ThemeContext';
import './Header.css';

const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const { getMCPServerCount, getWorkflowCount } = useCart();
  const { getProfileMCPServerCount, getProfileWorkflowCount } = useProfile();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Function to handle navigation clicks and scroll to top
  const handleNavClick = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Render logo as small icon only
  const renderLogo = () => {
    return (
      <div className="header-logo-container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        margin: 0,
        padding: 0,
        lineHeight: 0
      }}>
        <img 
          src={process.env.PUBLIC_URL + (isDarkMode ? '/images/AI-MCP-Bio_Logo_3_White.svg' : '/images/AI-MCP-Bio_Logo_3_Black.svg')} 
          alt="AI-MCP-Bio Logo"
          className="header-logo-image"
          style={{
            height: '56px',
            minHeight: '56px',
            width: 'auto',
            maxHeight: '56px',
            flexShrink: 0,
            margin: 0,
            padding: 0,
            display: 'block'
          }}
        />
        <span className="header-logo-text" style={{
          fontSize: '2.2rem',
          fontWeight: '600',
          color: isDarkMode ? '#66B3FF' : '#0099FF',
          lineHeight: '1',
          margin: 0,
          padding: 0
        }}>
          AI-MCP-Bio
        </span>
      </div>
    );
  };

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      background: isDarkMode ? '#2d2d2d' : '#fff',
      boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
      padding: '0.4rem 0',
      zIndex: 1000,
      transition: 'all 0.3s ease'
    }}>
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Desktop Layout */}
        <div className="header-desktop" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 8px', alignItems: 'center', gap: '8px', position: 'relative' }}>
          {/* Left Section - Logo */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            flex: '0 0 auto',
            minWidth: '0'
          }}>
            <Link to="/" className="header-logo" style={{
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
              minWidth: 'fit-content'
            }} onClick={handleNavClick}>
              <img 
                src={process.env.PUBLIC_URL + (isDarkMode ? '/images/AI-MCP-Bio_Logo_3_White.svg' : '/images/AI-MCP-Bio_Logo_3_Black.svg')} 
                alt="AI-MCP-Bio Logo"
                className="header-logo-image"
                style={{
                  height: '56px',
                  minHeight: '56px',
                  width: 'auto',
                  flexShrink: 0
                }}
              />
              <span className="header-logo-text" style={{
                fontSize: '2.2rem',
                fontWeight: '600',
                color: isDarkMode ? '#66B3FF' : '#0099FF',
                lineHeight: '1'
              }}>
                AI-MCP-Bio
              </span>
            </Link>
          </div>
          
          {/* Center Section - Navigation */}
          <nav className="header-nav" style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            justifyContent: 'center',
            minWidth: '0'
          }}>
            <Link to="/" style={{ 
              textDecoration: 'none', 
              color: '#9D4EDD',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px'
            }}
            title="Home" onClick={handleNavClick}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9D4EDD'
              }}>
                {/* Simple house icon with door */}
                <svg width="48" height="48" viewBox="0 0 32 32" fill="currentColor">
                  <path d="M16 4L4 14v14h8v-8h8v8h8V14L16 4zM20 20h-8v-6h8v6z"/>
                </svg>
              </div>
            </Link>
            <Link to="/mcp-servers" style={{ 
              textDecoration: 'none', 
              color: '#0099FF',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px'
            }}
            title="MCP Servers" onClick={handleNavClick}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0099FF'
              }}>
                {/* Simple server stack */}
                <svg width="60" height="48" viewBox="0 0 32 32" fill="currentColor">
                  <rect x="6" y="6" width="20" height="6" rx="1" fill="currentColor"/>
                  <rect x="6" y="14" width="20" height="6" rx="1" fill="currentColor"/>
                  <rect x="6" y="22" width="20" height="6" rx="1" fill="currentColor"/>
                </svg>
              </div>
            </Link>
            <Link to="/workflows" style={{ 
              textDecoration: 'none', 
              color: '#00CC66',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px'
            }}
            title="Workflows" onClick={handleNavClick}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00CC66'
              }}>
                {/* Connected nodes network icon */}
                <svg width="48" height="48" viewBox="0 0 32 32" fill="currentColor">
                  <rect x="12" y="4" width="8" height="8" rx="2" fill="currentColor"/>
                  <rect x="4" y="20" width="8" height="8" rx="2" fill="currentColor"/>
                  <rect x="20" y="20" width="8" height="8" rx="2" fill="currentColor"/>
                  <line x1="16" y1="12" x2="8" y2="20" stroke="currentColor" strokeWidth="2"/>
                  <line x1="16" y1="12" x2="24" y2="20" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="24" x2="24" y2="24" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </Link>
          </nav>
          
          {/* Right Section - User Controls */}
          <div className="header-actions" style={{ 
            display: 'flex', 
            gap: '4px', 
            alignItems: 'center',
            flex: '0 0 auto',
            justifyContent: 'flex-end',
            minWidth: '0'
          }}>
            {/* Theme Toggle Switch */}
            <button
              onClick={toggleTheme}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                color: isDarkMode ? '#e0e0e0' : '#000',
                backgroundColor: 'transparent',
                width: '32px',
                height: '32px'
              }}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                e.target.style.color = isDarkMode ? '#fff' : '#000';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = isDarkMode ? '#e0e0e0' : '#000';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                {isDarkMode ? (
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
                ) : (
                  <path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"/>
                )}
              </svg>
            </button>
            
            <div className="flex gap-10" style={{ alignItems: 'center' }}>
              
              {/* Admin Icon */}
              {user && isAdmin() ? (
                <Link to="/admin" style={{ 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  backgroundColor: 'transparent'
                }} title="Admin" onClick={handleNavClick}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Admin Dashboard Icon - Grid Layout */}
                    <div style={{
                      width: '28px',
                      height: '28px',
                      position: 'relative',
                      opacity: '1'
                    }}>
                      {/* Top row panels */}
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        left: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        left: '12px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      
                      {/* Middle row panels */}
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        left: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      
                      {/* Bottom row panels */}
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        left: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        left: '12px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        borderRadius: '1px'
                      }}></div>
                    </div>
                  </div>
                </Link>
              ) : (
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  cursor: 'default'
                }} title="Admin" onClick={handleNavClick}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: '0.15'
                  }}>
                    {/* Admin Dashboard Icon - Grid Layout */}
                    <div style={{
                      width: '28px',
                      height: '28px',
                      position: 'relative'
                    }}>
                      {/* Top row panels */}
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        left: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        left: '12px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      
                      {/* Middle row panels */}
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        left: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      
                      {/* Bottom row panels */}
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        left: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        left: '12px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: isDarkMode ? 'rgba(224, 224, 224, 0.2)' : 'rgba(51, 51, 51, 0.2)',
                        borderRadius: '1px'
                      }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Cart Icon - only visible when logged in */}
              {user && (
                <Link to="/cart" style={{ 
                  textDecoration: 'none',
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative'
                }} title="Cart" onClick={handleNavClick}>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={isDarkMode ? '#e0e0e0' : '#333'} 
                    strokeWidth="2.5"
                    style={{ width: '20px', height: '20px' }}
                  >
                    <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h8"/>
                    <circle cx="9" cy="19" r="1"/>
                    <circle cx="20" cy="19" r="1"/>
                  </svg>
                  {/* MCP Servers Counter - Blue (top-right) */}
                  <div className="counter-badge counter-top" style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#0099FF',
                    color: 'white',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '9px',
                    fontWeight: 'bold',
                  }}>
                    {getMCPServerCount()}
                  </div>
                  {/* Workflows Counter - Green (bottom-right) */}
                  <div className="counter-badge counter-bottom" style={{
                    position: 'absolute',
                    bottom: '-8px',
                    right: '-8px',
                    backgroundColor: '#00CC66',
                    color: 'white',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '9px',
                    fontWeight: 'bold',
                  }}>
                    {getWorkflowCount()}
                  </div>
                </Link>
              )}
              
              {/* User initials button - links to profile */}
              {user ? (
                <Link to="/profile" style={{
                  textDecoration: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: isDarkMode ? '#282c34' : 'white',
                  border: '1px solid #666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: isDarkMode ? 'white' : '#333',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                title="Profile Dashboard" onClick={handleNavClick}>
                  {user.username ? user.username.substring(0, 2).toUpperCase() : 'GO'}
                  {/* MCP Servers Counter - Blue (top-right) */}
                  <div className="counter-badge counter-top" style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#0099FF',
                    color: 'white',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '9px',
                    fontWeight: 'bold',
                  }}>
                    {getProfileMCPServerCount()}
                  </div>
                  {/* Workflows Counter - Green (bottom-right) */}
                  <div className="counter-badge counter-bottom" style={{
                    position: 'absolute',
                    bottom: '-8px',
                    right: '-8px',
                    backgroundColor: '#00CC66',
                    color: 'white',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '9px',
                    fontWeight: 'bold',
                  }}>
                    {getProfileWorkflowCount()}
                  </div>
                </Link>
              ) : (
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: isDarkMode ? '#282c34' : 'white',
                  border: '1px solid #666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: isDarkMode ? 'white' : '#333',
                  transition: 'all 0.3s ease'
                }}>
                  --
                </div>
              )}
              
              {/* Register button - inactive when logged in */}
              {user ? (
                <div 
                  style={{
                    backgroundColor: isDarkMode ? '#1a1a1a' : '#f5f5f5',
                    color: isDarkMode ? '#666' : '#999',
                    border: '1px solid #444',
                    borderRadius: '16px',
                    padding: '5px 12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'inline-block',
                    width: '70px',
                    textAlign: 'center',
                    cursor: 'not-allowed',
                    opacity: 0.6
                  }}
                  title="Already registered"
                >
                  Register
                </div>
              ) : (
                <Link 
                  to="/register" 
                  style={{
                    backgroundColor: isDarkMode ? '#282c34' : 'transparent',
                    color: isDarkMode ? 'white' : '#333',
                    border: '1px solid #666',
                    borderRadius: '16px',
                    padding: '5px 12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'all 0.3s ease',
                    width: '70px',
                    textAlign: 'center'
                  }}
                  onClick={handleNavClick}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#f5f5f5';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#282c34' : 'transparent';
                  }}
                >
                  Register
                </Link>
              )}
              
              {/* Show Login or Logout based on user status */}
              {user ? (
                <button 
                  onClick={handleLogout}
                  style={{
                    backgroundColor: isDarkMode ? '#282c34' : 'transparent',
                    color: isDarkMode ? 'white' : '#333',
                    border: '1px solid #666',
                    borderRadius: '16px',
                    padding: '5px 12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    width: '70px',
                    textAlign: 'center'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#f5f5f5';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#282c34' : 'transparent';
                  }}
                >
                  Logout
                </button>
              ) : (
                <Link 
                  to="/login" 
                  style={{
                    backgroundColor: isDarkMode ? '#282c34' : 'transparent',
                    color: isDarkMode ? 'white' : '#333',
                    border: '1px solid #666',
                    borderRadius: '16px',
                    padding: '5px 12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'all 0.3s ease',
                    width: '70px',
                    textAlign: 'center'
                  }}
                  onClick={handleNavClick}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#f5f5f5';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#282c34' : 'transparent';
                  }}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>


        {/* Mobile Layout - Three Rows */}
        <div className="header-mobile">
          {/* Row 1: Logo Only */}
          <div className="mobile-row-1">
            <Link to="/" className="mobile-logo" style={{
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              padding: 0,
              lineHeight: 0
            }} onClick={handleNavClick}>
              {renderLogo()}
            </Link>
          </div>
          {/* Row 2: Navigation Icons */}
          <div className="mobile-row-2">
            <Link to="/" style={{ 
              textDecoration: 'none', 
              color: '#9D4EDD',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px'
            }}
            title="Home" onClick={handleNavClick}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9D4EDD'
              }}>
                <svg width="48" height="48" viewBox="0 0 32 32" fill="currentColor">
                  <path d="M16 4L4 14v14h8v-8h8v8h8V14L16 4zM20 20h-8v-6h8v6z"/>
                </svg>
              </div>
            </Link>
            <Link to="/mcp-servers" style={{ 
              textDecoration: 'none', 
              color: '#0099FF',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px'
            }}
            title="MCP Servers" onClick={handleNavClick}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0099FF'
              }}>
                <svg width="60" height="48" viewBox="0 0 32 32" fill="currentColor">
                  <rect x="6" y="6" width="20" height="6" rx="1" fill="currentColor"/>
                  <rect x="6" y="14" width="20" height="6" rx="1" fill="currentColor"/>
                  <rect x="6" y="22" width="20" height="6" rx="1" fill="currentColor"/>
                </svg>
              </div>
            </Link>
            <Link to="/workflows" style={{ 
              textDecoration: 'none', 
              color: '#00CC66',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px'
            }}
            title="Workflows" onClick={handleNavClick}>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00CC66'
              }}>
                <svg width="48" height="48" viewBox="0 0 32 32" fill="currentColor">
                  <rect x="12" y="4" width="8" height="8" rx="2" fill="currentColor"/>
                  <rect x="4" y="20" width="8" height="8" rx="2" fill="currentColor"/>
                  <rect x="20" y="20" width="8" height="8" rx="2" fill="currentColor"/>
                  <line x1="16" y1="12" x2="8" y2="20" stroke="currentColor" strokeWidth="2"/>
                  <line x1="16" y1="12" x2="24" y2="20" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="24" x2="24" y2="24" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </Link>
          </div>

          {/* Row 3: User Controls */}
          <div className="mobile-row-3">
            {/* Theme Toggle Switch */}
            <button
              onClick={toggleTheme}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                color: isDarkMode ? '#e0e0e0' : '#000',
                backgroundColor: 'transparent',
                width: '32px',
                height: '32px'
              }}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                e.target.style.color = isDarkMode ? '#fff' : '#000';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = isDarkMode ? '#e0e0e0' : '#000';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                {isDarkMode ? (
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
                ) : (
                  <path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"/>
                )}
              </svg>
            </button>
            
            {/* Admin Icon */}
            {user && isAdmin() ? (
              <Link to="/admin" style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                backgroundColor: 'transparent'
              }} title="Admin" onClick={handleNavClick}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    position: 'relative',
                    opacity: '1'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: '2px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: '12px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '2px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '2px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      bottom: '2px',
                      left: '2px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      bottom: '2px',
                      left: '12px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      bottom: '2px',
                      right: '2px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      borderRadius: '1px'
                    }}></div>
                  </div>
                </div>
              </Link>
            ) : null}
            
            {/* Cart Icon */}
            {user && (
              <Link to="/cart" style={{ 
                textDecoration: 'none',
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative'
              }} title="Cart" onClick={handleNavClick}>
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke={isDarkMode ? '#e0e0e0' : '#333'} 
                  strokeWidth="2.5"
                  style={{ width: '24px', height: '24px' }}
                >
                  <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h8"/>
                  <circle cx="9" cy="19" r="1"/>
                  <circle cx="20" cy="19" r="1"/>
                </svg>
                {/* MCP Servers Counter - Blue (top-right) */}
                <div className="counter-badge counter-top" style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#0099FF',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 'bold'
                }}>
                  {getMCPServerCount()}
                </div>
                {/* Workflows Counter - Green (bottom-right) */}
                <div className="counter-badge counter-bottom" style={{
                  position: 'absolute',
                  bottom: '-8px',
                  right: '-8px',
                  backgroundColor: '#00CC66',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 'bold'
                }}>
                  {getWorkflowCount()}
                </div>
              </Link>
            )}
            
            {/* User Profile */}
            {user ? (
              <Link to="/profile" style={{
                textDecoration: 'none',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: isDarkMode ? '#282c34' : 'white',
                border: '1px solid #666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: isDarkMode ? 'white' : '#333',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative'
              }}
              title="Profile Dashboard">
                {user.username ? user.username.substring(0, 2).toUpperCase() : 'GO'}
                {/* MCP Servers Counter - Blue (top-right) */}
                <div className="counter-badge counter-top" style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#0099FF',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 'bold'
                }}>
                  {getProfileMCPServerCount()}
                </div>
                {/* Workflows Counter - Green (bottom-right) */}
                <div className="counter-badge counter-bottom" style={{
                  position: 'absolute',
                  bottom: '-8px',
                  right: '-8px',
                  backgroundColor: '#00CC66',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 'bold'
                }}>
                  {getProfileWorkflowCount()}
                </div>
              </Link>
            ) : (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: isDarkMode ? '#282c34' : 'white',
                border: '1px solid #666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                color: isDarkMode ? 'white' : '#333',
                transition: 'all 0.3s ease'
              }}>
                --
              </div>
            )}
            
            {/* Register/Login Buttons */}
            {user ? (
              <button 
                onClick={handleLogout}
                style={{
                  backgroundColor: isDarkMode ? '#282c34' : 'transparent',
                  color: isDarkMode ? 'white' : '#333',
                  border: '1px solid #666',
                  borderRadius: '25px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '100px',
                  textAlign: 'center'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#f5f5f5';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = isDarkMode ? '#282c34' : 'transparent';
                }}
              >
                Logout
              </button>
            ) : (
              <>
                <Link 
                  to="/register" 
                  style={{
                    backgroundColor: isDarkMode ? '#282c34' : 'transparent',
                    color: isDarkMode ? 'white' : '#333',
                    border: '1px solid #666',
                    borderRadius: '25px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'all 0.3s ease',
                    minWidth: '100px',
                    textAlign: 'center'
                  }}
                  onClick={handleNavClick}
                >
                  Register
                </Link>
                <Link 
                  to="/login" 
                  style={{
                    backgroundColor: isDarkMode ? '#282c34' : 'transparent',
                    color: isDarkMode ? 'white' : '#333',
                    border: '1px solid #666',
                    borderRadius: '25px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'all 0.3s ease',
                    minWidth: '100px',
                    textAlign: 'center'
                  }}
                  onClick={handleNavClick}
                >
                  Login
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
